
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquare, Loader2, Upload, AlertCircle } from 'lucide-react';
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Message {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
  dataContext?: string;
}

export const NaturalLanguageQuery = () => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [dataContext, setDataContext] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState<string>('');
  const { callAIService, isLoading } = useOpenAIService();

  // Load user's industry from profile if available
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            // Set a default industry based on profile data or use general
            setIndustry('general');
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, []);

  const handleSubmitQuery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a question');
      return;
    }

    if (!dataContext.trim()) {
      toast.error('Please provide data context first');
      return;
    }

    const currentQuery = query;
    setQuery('');
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to use natural language queries');
        return;
      }

      console.log('Processing natural language query:', {
        query: currentQuery,
        industry,
        dataContextLength: dataContext.length
      });

      const answer = await callAIService({
        feature: 'natural_language_query',
        query: currentQuery,
        data: dataContext,
        industry: industry || 'general',
        userId: user.id,
        interactionType: 'natural_language_query'
      });

      const newMessage: Message = {
        id: Date.now().toString(),
        question: currentQuery,
        answer,
        timestamp: new Date(),
        dataContext: dataContext.substring(0, 100) + '...' // Preview of data used
      };

      setMessages(prev => [newMessage, ...prev]);
      toast.success('Query processed successfully!');
    } catch (error) {
      console.error('Failed to process query:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process query';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleDataUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        setDataContext(content);
        toast.success('Data loaded successfully!');
      };
      reader.readAsText(file);
    }
  };

  const clearMessages = () => {
    setMessages([]);
    toast.success('Conversation cleared');
  };

  return (
    <Card className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Natural Language Query AI</h3>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-2">Industry Context</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue placeholder="Select industry for better context" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Data Context</label>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                type="file"
                accept=".csv,.json,.txt"
                onChange={handleDataUpload}
                className="hidden"
                id="data-upload"
              />
              <label
                htmlFor="data-upload"
                className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
              >
                <Upload className="h-4 w-4" />
                Upload Data File (CSV, JSON, TXT)
              </label>
            </div>
            <Textarea
              placeholder="Or paste your data here (CSV format, JSON, or describe your data structure)..."
              value={dataContext}
              onChange={(e) => setDataContext(e.target.value)}
              rows={4}
              className="resize-none"
            />
          </div>
          {dataContext && (
            <p className="text-xs text-muted-foreground mt-1">
              Data loaded: {dataContext.length} characters
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-4">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmitQuery} className="space-y-4 mb-6">
        <div className="flex gap-2">
          <Input
            placeholder="Ask a question about your data in plain English..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isLoading}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isLoading || !query.trim() || !dataContext.trim()}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Ask'
            )}
          </Button>
        </div>
        
        <div className="text-xs text-muted-foreground">
          Example questions: "What's the average sales per month?", "Which customers have the highest value?", "Show me trends over time"
        </div>
      </form>

      {messages.length > 0 && (
        <div className="flex justify-between items-center mb-4">
          <h4 className="font-medium">Conversation History</h4>
          <Button variant="outline" size="sm" onClick={clearMessages}>
            Clear History
          </Button>
        </div>
      )}

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {messages.length === 0 && !isLoading && (
          <div className="text-center text-muted-foreground py-8">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No questions asked yet. Upload your data and start asking questions!</p>
          </div>
        )}
        
        {messages.map((message) => (
          <div key={message.id} className="border rounded-lg p-4 bg-white">
            <div className="flex justify-between items-start mb-2">
              <div className="font-medium text-sm text-muted-foreground">
                {message.timestamp.toLocaleTimeString()}
              </div>
              {message.dataContext && (
                <div className="text-xs text-muted-foreground">
                  Data: {message.dataContext}
                </div>
              )}
            </div>
            <div className="font-medium mb-2 text-blue-700">
              Q: {message.question}
            </div>
            <div className="text-sm bg-muted p-3 rounded whitespace-pre-wrap">
              A: {message.answer}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
